import clsx from "clsx";
import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import AssistedSearch from "web/react/components/__LEGACY__/assisted-search/assisted-search";
import AutoComplete from "web/react/components/__LEGACY__/auto-complete/auto-complete";
import { AutoCompleteContext } from "web/react/components/__LEGACY__/auto-complete/auto-complete.context";
import { SearchButton } from "web/react/components/__LEGACY__/search-button/search-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import {
    closeAssistedSearch,
    closeSearch,
    openAssistedSearch,
    openSearch,
} from "web/redux/ducks/app-header-bar";
import { hideMainNavigation } from "web/redux/ducks/main-navigation";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { languageIsEnglish } from "web/script/modules/language";
import strings from "web/script/utils/strings";
import { AppHeaderBarSearchInputSerializer } from "web/types/serializers";
import styles from "./header-bar-search.module.css";

const EVENT_CATEGORY = "nav_search";

export interface HeaderBarSearchProps extends AppHeaderBarSearchInputSerializer {
    className?: string;
    isMobile?: boolean;
}

function extractHeaderSearchState(state: ReduxStoreState): boolean {
    return state.appHeaderBarReducer.isSearchOpen;
}

function extractSelectedGenderState(state: ReduxStoreState): string {
    return strings.capitalize(state.mainNavigationReducer.selectedGender);
}

function HeaderBarSearchBar({
    className,
    action_url,
    gender,
    query: initialQuery,
    isMobile,
}: HeaderBarSearchProps): React.ReactElement {
    const {
        query,
        isSearchInputFocused,
        setSearchInputFocused,
        updateAutocomplete,
        searchInputRef,
        onKeyDown,
        onSubmit,
        onInput,
        isVisible: autoCompleteIsVisible,
    } = React.useContext(AutoCompleteContext);
    const stateGender = useSelector(extractSelectedGenderState);
    const selectedGender = stateGender || gender;
    const [initialInteracted, setInitialInteracted] = React.useState<boolean>(false);
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const { isMobileViewport, isTabletViewport } = useDomViewport();
    const dispatch = useDispatch();

    function maybeOpenAssistedSearch(forceClose = false): void {
        if (
            autoCompleteIsVisible ||
            forceClose ||
            !languageIsEnglish() ||
            isMobileViewport ||
            isTabletViewport
        ) {
            dispatch(closeAssistedSearch());
        } else {
            dispatch(openAssistedSearch());
        }
    }

    useEffect(() => {
        if (initialInteracted) {
            maybeOpenAssistedSearch(autoCompleteIsVisible);
        }
    }, [autoCompleteIsVisible]);

    /* initialize the search value on initial load; query doesn't change */
    React.useEffect(() => {
        if (initialQuery) {
            updateAutocomplete({ query: initialQuery });
        }
        setLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialQuery]);

    React.useEffect(() => {
        updateAutocomplete({
            gender: selectedGender,
        });
    }, [updateAutocomplete, selectedGender]);

    function onBlurSearchInput(): void {
        setSearchInputFocused(false);
        dispatch(closeSearch());
    }

    function onFocusSearchInput(): void {
        setSearchInputFocused(true);
        dispatch(openSearch());

        if (!initialInteracted) {
            setInitialInteracted(true);
        }

        analytics.event(EVENT_CATEGORY, "search_input_focused");
        dispatch(hideMainNavigation());
        maybeOpenAssistedSearch();
    }

    function onClickCancelButton(): void {
        close();
        analytics.event("header", "clicked", "burger_menu_search_button_close");
    }

    return (
        <span className={clsx(styles.searchWrapper, className)}>
            <form
                className={clsx(styles.search, {
                    [styles.searchFocusedBorder]: isSearchInputFocused,
                })}
                action={action_url}
                onSubmit={onSubmit}
            >
                <span className={styles.searchIcon}>
                    <SVGIcon name="search" />
                </span>

                <input
                    type="text"
                    className="hidden"
                    name="term"
                    defaultValue={loaded ? query : initialQuery}
                />
                <input type="text" className="hidden" name="gender" defaultValue={selectedGender} />

                <div className={styles.inputContainer}>
                    <button type="submit" className={styles.submitButton}>
                        <SVGIcon name="search" />
                    </button>

                    {/* set autoComplete="off" autoCorrect="off" to disable QuickType on iOS */}
                    <input
                        aria-label={gettext("header.product_search.label")}
                        type="search"
                        className={clsx(styles.input, {
                            [styles.lowercasePlaceholder]: !!stateGender,
                        })}
                        autoComplete="off"
                        autoCorrect="off"
                        name="q"
                        placeholder={
                            !isMobile
                                ? selectedGender === "Men"
                                    ? gettext("header.search.placeholder_for_men")
                                    : gettext("header.search.placeholder_for_women")
                                : undefined
                        }
                        onBlur={onBlurSearchInput}
                        onFocus={onFocusSearchInput}
                        onInput={onInput}
                        onChange={onInput}
                        onKeyDown={onKeyDown}
                        ref={searchInputRef}
                        value={loaded ? query : initialQuery}
                        autoFocus={isMobile}
                    />
                    <button
                        aria-label={gettext("header.product_search_submit.label")}
                        type="button"
                        className={clsx(styles.arrowButton, {
                            hidden: initialInteracted ? (loaded ? !query : !initialQuery) : true,
                        })}
                        onMouseDown={onSubmit}
                    >
                        <SVGIcon name="arrow-right" />
                    </button>
                </div>

                {/* Native keyboard `search` button wouldn't work w/o this w/o displaying a [type=submit] element (can't have display: none) */}
                <input className={styles.submitInput} type="submit" />

                <div
                    className={clsx(styles.autocompleteWrapper, {
                        hidden: !isSearchInputFocused,
                    })}
                >
                    <AutoComplete />
                </div>
            </form>
            <AssistedSearch defaultGender={gender} />
            <button className={styles.cancelButton} type="button" onClick={onClickCancelButton}>
                <SVGIcon className={styles.cancelIcon} name="cross-thin" />
            </button>
        </span>
    );
}

function HeaderBarSearch(props: HeaderBarSearchProps): React.ReactElement {
    const showMobileAppHeaderSearch = useSelector(extractHeaderSearchState);

    return (
        <>
            <span className="hidden-desktop">
                <SearchButton />
            </span>
            <HeaderBarSearchBar {...props} className="hidden-tablet" />
            {showMobileAppHeaderSearch &&
                ReactDOM.createPortal(
                    <HeaderBarSearchBar {...props} className="hidden-desktop" isMobile />,
                    document.getElementById("app-header-search-bar-mobile") as HTMLDivElement
                )}
        </>
    );
}

export default HeaderBarSearch;
